import Add from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import {ComponentProps, ElementType, FC, useState} from 'react';
import Markdown, {ExtraProps} from 'react-markdown';
import {SuperResDismissibleChip} from '@src/components/visualize/layers/SuperResDismissibleChip';
import {Layer} from '@src/types/configurations';

type LayerAccordionOnClick =
  | ((layerId: string | undefined, e: React.MouseEvent) => void)
  | (() => void);

type LayerAccordionItemProps = {
  onClick: LayerAccordionOnClick;
  layer: Layer;
  isSelected?: boolean;
};

const heading: ElementType<ComponentProps<'h1'> & ExtraProps> = ({
  node,
  ...rest
}) => {
  return <Typography variant="h6" {...rest} />;
};

const LayerDescription: FC<{children: string}> = ({children}) => {
  return (
    <Markdown
      components={{
        h1: heading,
        h2: heading,
        h3: heading,
        h4: heading,
        h5: heading,
        h6: heading,
        a({node, ...rest}) {
          return <Link target="_blank" rel="noopener noreferrer" {...rest} />;
        },
      }}
    >
      {children}
    </Markdown>
  );
};

export const LayerAccordionItem = ({
  onClick,
  layer,
  isSelected,
}: LayerAccordionItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Accordion
      elevation={isSelected ? 4 : 0}
      sx={{
        '&.Mui-expanded': {
          marginInline: 0,
          marginBlock: 0,
          '&:before': {
            opacity: 1,
          },
        },
      }}
      expanded={isSelected && isExpanded}
    >
      <AccordionSummary
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
          },
        }}
        onClick={e => onClick(layer?.id, e)}
      >
        <Paper
          elevation={8}
          sx={{
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            inlineSize: 36,
            blockSize: 36,
            borderRadius: '50%',
            marginInlineEnd: 2,
            fontSize: 15,
            borderWidth: isSelected ? 2 : 0,
            borderStyle: 'solid',
            borderColor: 'primary.main',
          }}
        >
          {layer?.title.charAt(0) ?? <Add color="action" />}
        </Paper>
        <Typography
          sx={{
            fontSize: 14,
            color: isSelected ? 'text.primary' : 'text.secondary',
          }}
        >
          {layer?.title ?? 'Create custom visualization'}
        </Typography>
        {isSelected && layer?.description && (
          <ExpandMoreIcon
            sx={theme => ({
              display: !layer ? 'none' : 'block',
              color: isSelected ? 'text.primary' : 'text.secondary',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }),
              marginInline: 1,
            })}
            onClick={handleExpandClick}
          />
        )}
        <SuperResDismissibleChip layer={layer} />
      </AccordionSummary>

      <AccordionDetails sx={{typography: 'body2', color: 'text.secondary'}}>
        <LayerDescription>{layer?.description ?? ''}</LayerDescription>
      </AccordionDetails>
    </Accordion>
  );
};

export default LayerAccordionItem;
